import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';
import ScopeDiagramPicker from './ScopeDiagramPicker';
import ReviewCommentsCallout, { type ReviewCommentWithReplies } from '@/components/review/ReviewCommentsCallout';
import type { NoteType } from '@/types/note';

const Link = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
);
const mdComponents = { a: Link };

export interface NoteSectionCardProps {
  type: NoteType;
  label: string;
  draft: string;
  isEditing: boolean;
  onDraftChange: (value: string) => void;
  onToggleEdit: () => void;
  // AI props (optional per section)
  isEspecial: boolean;
  onAiGenerate?: () => void;
  isAiGenerating?: boolean;
  aiLabel?: string;
  // Scope picker props (optional per section)
  diagrams?: { name: string }[];
  defaultDiagramIndices?: number[];
  scopePickerLabel?: string;
  onScopeInsert?: (indices: number[]) => void;
  reviewComments?: ReviewCommentWithReplies[];
}

export default function NoteSectionCard({
  type,
  label,
  draft,
  isEditing,
  onDraftChange,
  onToggleEdit,
  isEspecial,
  onAiGenerate,
  isAiGenerating,
  aiLabel,
  diagrams,
  defaultDiagramIndices,
  scopePickerLabel,
  onScopeInsert,
  reviewComments = [],
}: NoteSectionCardProps) {
  return (
    <Card key={type}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{label}</CardTitle>
          <div className="flex gap-2">
            {onAiGenerate && isEspecial && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAiGenerate}
                disabled={isAiGenerating}
              >
                {isAiGenerating ? (
                  aiLabel ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {aiLabel}
                    </>
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    AI
                  </>
                )}
              </Button>
            )}
            {isEditing && onScopeInsert && diagrams && defaultDiagramIndices && scopePickerLabel && (
              <ScopeDiagramPicker
                label={scopePickerLabel}
                diagrams={diagrams}
                defaultIndices={defaultDiagramIndices}
                onInsert={onScopeInsert}
              />
            )}
            <Button variant="outline" size="sm" onClick={onToggleEdit}>
              {isEditing ? 'Preview' : 'Edit'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <ReviewCommentsCallout comments={reviewComments} />
        {isEditing ? (
          <Textarea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
            placeholder={`Enter ${label.toLowerCase()} notes in Markdown format...`}
          />
        ) : draft.trim() ? (
          <div className="prose prose-sm dark:prose-invert max-w-none prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-table:rounded-lg prose-td:px-3 prose-th:px-3">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={mdComponents}
            >
              {draft}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">No {label.toLowerCase()} notes.</p>
        )}
      </CardContent>
    </Card>
  );
}
